import { Component, computed, Input, ViewChild, signal } from '@angular/core'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { LoggableHistoryService } from '../../../services/entity-services/loggableHistory.service'
import { LoggableHistoryChange, LoggableHistoryEntry } from 'depoto-core/src/entities'
import { SchemaUtil } from '../../../utils'
import { CoreService } from '../../../services'
import { TranslateService } from '@ngx-translate/core'

// A change's value is a relation to another entity when the backend tells us so via
// relatedClass/relatedId, or - as a fallback - when the value itself comes back as an
// object with an id (the field name is then used as a best-effort guess of the entity class).
interface Relation {
  relatedClass: string
  id: string | number
}

// Most entities expose a plain "name" field, but some (e.g. Address) don't - they're
// identified by a composed label instead. This lets resolveRelatedNames() ask for the
// right fields per entity class rather than assuming "name" always exists.
const defaultRelatedEntityResolver = {
  schema: { id: null, name: null },
  getName: (entity: any): string | undefined => entity?.name,
}

const relatedEntityResolvers: Record<string, { schema: any; getName: (entity: any) => string | undefined }> = {
  Address: {
    schema: { id: null, firstName: null, lastName: null, companyName: null },
    getName: (entity: any): string | undefined =>
      entity?.companyName || [entity?.firstName, entity?.lastName].filter(Boolean).join(' ') || undefined,
  },
  // Order has no "name" field - querying it errors. Identify an order by its reservation
  // number, or its bill number when it isn't a reservation.
  Order: {
    schema: { id: null, reservationNumber: null, billNumber: null },
    getName: (entity: any): string | undefined => {
      const number = entity?.reservationNumber || entity?.billNumber
      return number ? String(number) : undefined
    },
  },
}

function getRelatedEntityResolver(relatedClass: string) {
  return relatedEntityResolvers[relatedClass] ?? defaultRelatedEntityResolver
}

// Entity classes that actually have a loggable-history view in the app (a page that mounts
// <modal-loggable-history [objectClass]="...">). Drill-in links are only offered for these -
// opening history for any other class would just show an empty modal. Keep in sync with the
// pages that render <modal-loggable-history>: checkout, depot, payment, customer, order,
// product, company-settings, vat, purchase-order.
const LOGGABLE_HISTORY_CLASSES = new Set<string>([
  'Checkout',
  'Depot',
  'Payment',
  'Customer',
  'Order',
  'Product',
  'Company',
  'Vat',
  'PurchaseOrder',
])

// One object whose history is currently (or was previously) shown. The modal keeps a stack
// of these so the user can drill into a related object's history and step back out again.
interface HistoryTarget {
  objectClass: string
  objectId: number | string
  // Human label for the header/back row - the related entity's resolved name when we have it,
  // otherwise a "ClassName #id" placeholder. Undefined for the root target opened from a page.
  label?: string
}

@Component({
  selector: 'modal-loggable-history',
  templateUrl: 'modal-loggable-history.component.html',
  styleUrls: ['modal-loggable-history.component.scss'],
  standalone: false,
})
export class ModalLoggableHistoryComponent {
  @ViewChild('modal') childModal: ModalContainerComponent
  @Input() objectClass: string
  @Input() objectId: number | string

  entries = signal<LoggableHistoryEntry[]>([])
  loading = signal(false)
  loadingMore = signal(false)
  hasMore = signal(false)
  private page = signal(1)

  // Navigation stack. Index 0 is the object the modal was opened for; each drill-in into a
  // related object's history pushes a new entry, and the back button pops it.
  private stack = signal<HistoryTarget[]>([])
  current = computed<HistoryTarget | undefined>(() => {
    const stack = this.stack()
    return stack[stack.length - 1]
  })
  canGoBack = computed(() => this.stack().length > 1)
  // Appended after the translated "History" header title so the header always names the object
  // whose history is on screen — the one the modal was opened for, and each object drilled into.
  titleSuffix = computed(() => {
    const target = this.current()
    if (!target) return ''
    return `— ${this.targetLabel(target)}`
  })

  // objectClass -> its name (e.g. Supplier #12 -> "ACME s.r.o."), resolved on demand via getById.
  relatedNames = signal<Map<string, string>>(new Map())
  private pendingRelatedKeys = new Set<string>()

  constructor(
    private loggableHistoryService: LoggableHistoryService,
    private core: CoreService,
    private translateService: TranslateService
  ) {}

  async showChildModal() {
    this.stack.set([{ objectClass: this.objectClass, objectId: this.objectId }])
    this.resetListState()
    this.childModal.showChildModal()
    await this.fetchPage(1)
  }

  hideChildModal() {
    this.childModal.hideChildModal()
  }

  loadMore() {
    if (this.loading() || this.loadingMore() || !this.hasMore()) return
    this.fetchPage(this.page() + 1)
  }

  // Drill into the history of the related object referenced by this change.
  async openRelated(change: LoggableHistoryChange) {
    if (this.loading() || this.loadingMore()) return
    const relation = this.getRelation(change)
    if (!relation || !LOGGABLE_HISTORY_CLASSES.has(relation.relatedClass)) return
    this.stack.update(stack => [
      ...stack,
      {
        objectClass: relation.relatedClass,
        objectId: relation.id,
        // Snapshot the name if we already have it; otherwise leave it undefined and let
        // targetLabel() fall back to "Type #id" until resolveTargetName() fills it in.
        label: this.relatedNames().get(this.relationKey(relation)),
      },
    ])
    this.resetListState()
    await this.fetchPage(1)
  }

  // Step back to the previous object's history.
  async goBack() {
    if (this.loading() || this.loadingMore() || !this.canGoBack()) return
    this.stack.update(stack => stack.slice(0, -1))
    this.resetListState()
    await this.fetchPage(1)
  }

  // Whether a change points at an object we can show history for. Requires both that the
  // related entity's name is resolved (so the link text is a real name, never a "ClassName #id"
  // placeholder) and that the class is one that actually has a loggable-history view.
  canOpenHistory(change: LoggableHistoryChange): boolean {
    const relation = this.getRelation(change)
    if (!relation || !LOGGABLE_HISTORY_CLASSES.has(relation.relatedClass)) return false
    return this.relatedNames().has(this.relationKey(relation))
  }

  // Header/breadcrumb label for a target: the localised entity type followed by the object's
  // own name (e.g. "Produkt Nike Air"), falling back to "Produkt #123" until the name resolves.
  private targetLabel(target: HistoryTarget): string {
    const type = this.entityTypeLabel(target.objectClass)
    const name =
      target.label ??
      this.relatedNames().get(this.relationKey({ relatedClass: target.objectClass, id: target.objectId }))
    return name ? `${type} ${name}` : `${type} #${target.objectId}`
  }

  // Localised singular name of an entity class ("Product" -> "Produkt"). Falls back to the raw
  // class name when no loggableHistory.entity.<Class> translation is registered.
  private entityTypeLabel(objectClass: string): string {
    if (!objectClass) return ''
    const key = `loggableHistory.entity.${objectClass}`
    const translated = this.translateService.instant(key)
    return translated === key ? objectClass : translated
  }

  // Resolve the target object's own display name (the object the modal was opened for, or the
  // one just drilled into) via the same getById + resolver path used for in-change relations,
  // and cache it in relatedNames so targetLabel() and the header pick it up. No-op once cached
  // or already in flight, or when the entity's service has no getById.
  private async resolveTargetName(target: HistoryTarget): Promise<void> {
    const relation: Relation = { relatedClass: target.objectClass, id: target.objectId }
    const key = this.relationKey(relation)
    if (this.relatedNames().has(key) || this.pendingRelatedKeys.has(key)) return

    const services = this.core.services as unknown as Record<
      string,
      { getById?: (id: number, schema: any) => Promise<any> }
    >
    const serviceKey = relation.relatedClass.charAt(0).toLowerCase() + relation.relatedClass.slice(1)
    if (typeof services[serviceKey]?.getById !== 'function') return

    this.pendingRelatedKeys.add(key)
    try {
      const resolver = getRelatedEntityResolver(relation.relatedClass)
      const entity = await services[serviceKey].getById(Number(relation.id), resolver.schema)
      const name = resolver.getName(entity)
      if (name) {
        const resolved = new Map(this.relatedNames())
        resolved.set(key, name)
        this.relatedNames.set(resolved)
      }
    } catch {
      // Leave it unresolved — targetLabel() keeps the "Type #id" fallback.
    } finally {
      this.pendingRelatedKeys.delete(key)
    }
  }

  private resetListState() {
    this.entries.set([])
    this.hasMore.set(false)
    this.page.set(1)
  }

  private async fetchPage(page: number) {
    const target = this.current()
    if (!target) return
    if (page === 1) this.resolveTargetName(target)
    this.loading.set(page === 1)
    this.loadingMore.set(page > 1)
    try {
      const result = await this.loggableHistoryService.getLoggableHistory(
        { objectClass: target.objectClass, objectId: target.objectId, page, sort: 'version', direction: 'desc' },
        SchemaUtil.loggableHistory
      )
      const items = (result.items ?? []).map(entry => {
        // Drop the soft-delete bookkeeping field ("deleted", shown as "Smazáno") from every
        // change set. The backend emits it on nearly all entries regardless of its value
        // (null, "" or a timestamp, interchangeably) and it's just noise for the reader.
        entry.changes = (entry.changes ?? []).filter(
          change => (change.field ?? '').toLowerCase() !== 'deleted'
        ) as typeof entry.changes
        return entry
      })
      this.entries.update(entries => (page === 1 ? items : [...entries, ...items]))
      this.page.set(page)
      this.hasMore.set(Number(result.paginator?.current) < Number(result.paginator?.endPage))
      this.resolveRelatedNames(items)
    } finally {
      this.loading.set(false)
      this.loadingMore.set(false)
    }
  }

  actionBadgeClass(action: string): string {
    switch (action) {
      case 'create':
        return 'bg-success'
      case 'delete':
        return 'bg-danger'
      default:
        return 'bg-primary'
    }
  }

  // Display text for a single change: the related entity's name once resolved, otherwise
  // a "ClassName #id" placeholder for a relation, or the raw value for a plain scalar change.
  displayValue(change: LoggableHistoryChange): string {
    const relation = this.getRelation(change)
    if (!relation) return this.rawDisplayValue(change.value)
    const name = this.relatedNames().get(this.relationKey(relation))
    return name ?? `${relation.relatedClass} #${relation.id}`
  }

  private rawDisplayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return ''
    // change.value comes back as a plain string, but object-shaped values (relations, PHP
    // DateTime's { date, timezone, timezone_type }, ...) are themselves JSON-encoded within it.
    let parsed = value
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value)
      } catch {
        parsed = value
      }
    }
    if (parsed && typeof parsed === 'object' && typeof (parsed as any).date === 'string') {
      // Strip the PHP DateTime microseconds suffix, e.g. "14:59:00.000000" -> "14:59:00".
      return (parsed as any).date.replace(/\.\d+$/, '')
    }
    if (typeof parsed === 'boolean') {
      return this.translateService.instant(String(parsed))
    }
    if (typeof parsed === 'object') {
      try {
        return JSON.stringify(parsed)
      } catch {
        return String(parsed)
      }
    }
    return String(parsed)
  }

  private getRelation(change: LoggableHistoryChange): Relation | null {
    if (change.relatedClass && change.relatedId) {
      return { relatedClass: change.relatedClass, id: change.relatedId }
    }
    let value: unknown = change.value
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value)
      } catch {
        return null
      }
    }
    if (value && typeof value === 'object' && (value as any).id != null) {
      const guessedClass = change.field ? change.field.charAt(0).toUpperCase() + change.field.slice(1) : ''
      return guessedClass ? { relatedClass: guessedClass, id: (value as any).id } : null
    }
    return null
  }

  private relationKey(relation: Relation): string {
    return `${relation.relatedClass}:${relation.id}`
  }

  private async resolveRelatedNames(items: LoggableHistoryEntry[]) {
    const services = this.core.services as unknown as Record<
      string,
      { getById?: (id: number, schema: any) => Promise<any> }
    >
    const toFetch = new Map<string, Relation>()
    for (const entry of items) {
      for (const change of entry.changes ?? []) {
        const relation = this.getRelation(change)
        if (!relation) continue
        const key = this.relationKey(relation)
        if (this.relatedNames().has(key) || this.pendingRelatedKeys.has(key)) continue
        const serviceKey = relation.relatedClass.charAt(0).toLowerCase() + relation.relatedClass.slice(1)
        if (typeof services[serviceKey]?.getById !== 'function') continue
        toFetch.set(key, relation)
      }
    }
    if (toFetch.size === 0) return

    toFetch.forEach((_, key) => this.pendingRelatedKeys.add(key))
    const results = await Promise.allSettled(
      Array.from(toFetch.entries()).map(async ([key, relation]) => {
        const serviceKey = relation.relatedClass.charAt(0).toLowerCase() + relation.relatedClass.slice(1)
        const resolver = getRelatedEntityResolver(relation.relatedClass)
        const entity = await services[serviceKey].getById(Number(relation.id), resolver.schema)
        return { key, name: resolver.getName(entity) }
      })
    )
    const resolved = new Map(this.relatedNames())
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value.name) {
        resolved.set(result.value.key, result.value.name)
      }
    }
    this.relatedNames.set(resolved)
    toFetch.forEach((_, key) => this.pendingRelatedKeys.delete(key))
  }
}
